/**
 * VenueRenderer.ts — Stage 8: The Venue Circuit
 *
 * Draws per-frame visual indicators for each VenueNode and FanBuildSite.
 *
 * Venues are rendered as proper 3D isometric building blocks — a coloured
 * topper that sits on the existing terrain tile at buildingCol/buildingRow,
 * with darker shaded side-faces so they read as solid structures:
 *
 *   unclaimed — grey translucent mini-block (half height)
 *   claiming  — blue-tinted block growing from grey → venue colour as progress fills
 *   claimed   — full-height coloured building; flashes red when under attack
 *
 * Labels (name, cost / bonus / status) float above the building north-point.
 * HP bar appears above the block only when the venue has taken damage.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { VenueNode, FanBuildSite } from '../systems/BuildSystem';
import type { TileMap } from '../map/TileMap';
import { ISO_TILE_WIDTH, ISO_TILE_HEIGHT, ELEVATION_STEP } from '../constants';

// ── Iso layout constants ──────────────────────────────────────────────────────
const HW   = ISO_TILE_WIDTH  / 2;  // 32 px  half diamond width
const HH   = ISO_TILE_HEIGHT / 2;  // 16 px  half diamond height
const STEP = ELEVATION_STEP;       // 16 px  per elevation level

// Building footprint scale — shrinks each building so adjacent venues on
// neighbouring tiles don't visually merge into one solid mass.
const BSCALE = 0.76;          // fraction of the tile diamond to fill
const BW     = HW * BSCALE;  // building half-width  (~24 px)
const BH     = HH * BSCALE;  // building half-height (~12 px)

// Extra height added ON TOP of the underlying terrain tile for each state.
const TOPPER_CLAIMED  = 1.5; // elevation levels
const TOPPER_CLAIMING = 0.8;
const TOPPER_EMPTY    = 0.4;

// ── Colour palette ────────────────────────────────────────────────────────────
const COLOUR_UNCLAIMED  = 0x555566;
const COLOUR_CLAIMING   = 0x3366dd;
const COLOUR_REP        = 0xffaa22; // amber  — reputation
const COLOUR_SALES      = 0x44cc66; // green  — sales
const COLOUR_FAN        = 0x22ddcc; // cyan   — fanbase
const COLOUR_BUILD_IDLE = 0xba68c8; // purple — idle workshop
const COLOUR_BUILD_WORK = 0xffd700; // gold   — active workshop
const OUTLINE           = 0x070a0e;

// ── Text styles ───────────────────────────────────────────────────────────────
const NAME_GREY = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   10,
  fill:       '#777788',
  align:      'center',
});
const NAME_BLUE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   10,
  fill:       '#66aaff',
  align:      'center',
});
const NAME_WHITE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   10,
  fill:       '#ffffff',
  align:      'center',
});
const INFO_STYLE = new TextStyle({
  fontFamily: 'Menlo, Consolas, monospace',
  fontSize:   9,
  fill:       '#aaaaaa',
  align:      'center',
});

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Multiply an RGB hex colour by a brightness factor [0..1]. */
function shade(color: number, factor: number): number {
  const r = Math.min(255, Math.round(((color >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((color >> 8)  & 0xff) * factor));
  const b = Math.min(255, Math.round((color & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
}

/** Linear interpolate between two colours by t ∈ [0, 1]. */
function lerpColor(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  return (
    (Math.round(ar + (br - ar) * t) << 16) |
    (Math.round(ag + (bg - ag) * t) << 8)  |
     Math.round(ab + (bb - ab) * t)
  );
}

/** Pick the dominant income colour for a claimed venue. */
function venueColour(v: VenueNode): number {
  if (v.fanBonus   > 0) return COLOUR_FAN;
  if (v.salesBonus > 0) return COLOUR_SALES;
  return COLOUR_REP;
}

// ── Renderer ──────────────────────────────────────────────────────────────────

interface VenueLabel { name: Text; info: Text; }

export class VenueRenderer {
  private readonly g      = new Graphics(); // venue geometry
  private readonly buildG = new Graphics(); // build-site geometry
  private readonly labels:      VenueLabel[] = [];
  private readonly buildLabels: VenueLabel[] = [];

  constructor(
    private readonly world: Container,
    private readonly map:   TileMap,
  ) {
    world.addChild(this.g);
    world.addChild(this.buildG);
  }

  render(venues: VenueNode[], elapsed: number): void {
    const g = this.g;
    g.clear();

    while (this.labels.length < venues.length) {
      const name = new Text('', NAME_GREY);
      const info = new Text('', INFO_STYLE);
      name.anchor.set(0.5, 1);
      info.anchor.set(0.5, 0);
      this.world.addChild(name, info);
      this.labels.push({ name, info });
    }

    // Painter's order: draw back-to-front so near tiles occlude far ones
    const order = venues
      .map((v, i) => ({ v, i }))
      .sort((a, b) =>
        (a.v.buildingCol + a.v.buildingRow) - (b.v.buildingCol + b.v.buildingRow),
      );

    for (const { v: venue, i } of order) {
      const label = this.labels[i];
      switch (venue.state) {
        case 'unclaimed': this.drawUnclaimed(g, venue, label); break;
        case 'claiming':  this.drawClaiming (g, venue, label); break;
        case 'claimed':   this.drawClaimed  (g, venue, label, elapsed); break;
      }
    }
  }

  // ── State renderers ───────────────────────────────────────────────────────

  private drawUnclaimed(g: Graphics, venue: VenueNode, label: VenueLabel): void {
    // Small grey ghost-block at the building tile
    this.drawIsoBlock(g, venue.buildingCol, venue.buildingRow,
      TOPPER_EMPTY, COLOUR_UNCLAIMED, 0.38);

    // Access tile: thin diamond outline (shows where to stand)
    const ac = this.map.tileTopCenter(venue.col, venue.row);
    g.lineStyle(1, COLOUR_UNCLAIMED, 0.45);
    this.drawDiamond(g, ac.x, ac.y);
    g.lineStyle(0);

    const top = this.buildingTopPoint(venue.buildingCol, venue.buildingRow, TOPPER_EMPTY);
    label.name.style = NAME_GREY;
    label.name.text  = venue.name;
    label.name.position.set(top.x, top.y - 4);
    label.info.text  = venue.claimCostRep === 0 && venue.claimCostSales === 0
      ? 'FREE'
      : `${venue.claimCostRep}REP ${venue.claimCostSales}SALES`;
    label.info.position.set(top.x, top.y + 6);
  }

  private drawClaiming(g: Graphics, venue: VenueNode, label: VenueLabel): void {
    const baseColour = venueColour(venue);
    const colour     = lerpColor(COLOUR_CLAIMING, baseColour, venue.progress * 0.6);
    const h          = TOPPER_CLAIMING + venue.progress * (TOPPER_CLAIMED - TOPPER_CLAIMING);

    this.drawIsoBlock(g, venue.buildingCol, venue.buildingRow, h, colour, 0.80);

    // Progress arc centred on the building top face
    const topPt  = this.buildingTopPoint(venue.buildingCol, venue.buildingRow, h);
    const arcCY  = topPt.y + HH * 0.5; // slightly south of north-point on top face
    const radius = HW * 0.38;
    g.lineStyle(0);
    g.beginFill(colour, 0.30);
    g.moveTo(topPt.x, arcCY);
    g.arc(topPt.x, arcCY, radius,
      -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * venue.progress);
    g.closePath();
    g.endFill();
    g.lineStyle(1.5, colour, 0.9);
    g.drawCircle(topPt.x, arcCY, radius);
    g.lineStyle(0);

    label.name.style = NAME_BLUE;
    label.name.text  = venue.name;
    label.name.position.set(topPt.x, topPt.y - 4);
    label.info.text  = `booking… ${Math.round(venue.progress * 100)}%`;
    label.info.position.set(topPt.x, topPt.y + 6);
  }

  private drawClaimed(
    g: Graphics, venue: VenueNode, label: VenueLabel, elapsed: number,
  ): void {
    const hpFrac     = venue.hp / venue.maxHp;
    const baseColour = venueColour(venue);

    // Attack flash: pulse quickly between venue colour and red
    let drawColour = baseColour;
    if (venue.isUnderAttack) {
      const pulse = (Math.sin(elapsed * Math.PI * 6) + 1) / 2;
      drawColour  = lerpColor(baseColour, 0xff2222, 0.30 + pulse * 0.50);
    } else if (hpFrac < 0.5) {
      drawColour = lerpColor(baseColour, 0xff2222, 1 - hpFrac * 2);
    }

    this.drawIsoBlock(g, venue.buildingCol, venue.buildingRow,
      TOPPER_CLAIMED, drawColour, 1.0);

    const top = this.buildingTopPoint(venue.buildingCol, venue.buildingRow, TOPPER_CLAIMED);

    // Rooftop antenna — a small signal mast on the north-point
    const antX = top.x;
    const antY = top.y - 2;
    g.lineStyle(1.5, shade(drawColour, 1.6), 0.75);
    g.moveTo(antX,     antY);
    g.lineTo(antX,     antY - 10);
    g.moveTo(antX - 5, antY - 6);
    g.lineTo(antX + 5, antY - 6);
    g.lineStyle(0);

    // Bonus-type colour dots
    this.drawBonusDots(g, top.x, top.y - 18, venue);

    // HP bar — only when damaged
    if (hpFrac < 0.99) {
      const barW = HW * 1.8;
      const barX = top.x - barW / 2;
      const barY = top.y - 26;
      g.lineStyle(0);
      g.beginFill(0x181818, 0.85);
      g.drawRect(barX, barY, barW, 4);
      g.endFill();
      const fillCol = hpFrac > 0.5 ? 0x44cc44 : hpFrac > 0.25 ? 0xffaa22 : 0xff3333;
      g.beginFill(fillCol);
      g.drawRect(barX, barY, barW * hpFrac, 4);
      g.endFill();
    }

    // Name and bonus labels
    const labelY = hpFrac < 0.99 ? top.y - 32 : top.y - 22;
    label.name.style = NAME_WHITE;
    label.name.text  = venue.name;
    label.name.position.set(top.x, labelY);
    label.info.text  = this.bonusLabel(venue);
    label.info.position.set(top.x, labelY + 10);
  }

  // ── 3D block drawing ──────────────────────────────────────────────────────

  /**
   * Draw an isometric block ON TOP OF the existing terrain at (col, row).
   *
   * `extraLevels` elevation units are added above the terrain's top face.
   * Three faces: SE (medium shade), SW (dark shade), top (full colour).
   */
  private drawIsoBlock(
    g: Graphics,
    col: number, row: number,
    extraLevels: number,
    topColour: number,
    alpha = 1.0,
  ): void {
    const base = this.map.tileBase(col, row);
    const elev = this.map.elevation(col, row);
    const EY   = base.y - elev * STEP; // Y of terrain top-face centre
    const h    = extraLevels * STEP;   // pixel height of our extra block

    // SE face (right — medium shade)
    g.lineStyle(0);
    g.beginFill(shade(topColour, 0.60), alpha);
    g.drawPolygon([
      base.x + BW, EY - h,
      base.x,      EY - h + BH,
      base.x,      EY + BH,
      base.x + BW, EY,
    ]);
    g.endFill();

    // SW face (left — dark shade)
    g.beginFill(shade(topColour, 0.38), alpha);
    g.drawPolygon([
      base.x - BW, EY - h,
      base.x,      EY - h + BH,
      base.x,      EY + BH,
      base.x - BW, EY,
    ]);
    g.endFill();

    // Top face diamond (full colour, faint outline)
    g.lineStyle(1, OUTLINE, 0.35);
    g.beginFill(topColour, alpha);
    g.drawPolygon([
      base.x,      EY - h - BH,
      base.x + BW, EY - h,
      base.x,      EY - h + BH,
      base.x - BW, EY - h,
    ]);
    g.endFill();
    g.lineStyle(0);
  }

  /**
   * World-space north-point of the top face of a block drawn with drawIsoBlock.
   * Use for label and decoration positioning.
   */
  private buildingTopPoint(
    col: number, row: number, extraLevels: number,
  ): { x: number; y: number } {
    const base = this.map.tileBase(col, row);
    const elev = this.map.elevation(col, row);
    return { x: base.x, y: base.y - elev * STEP - extraLevels * STEP - BH };
  }

  /** Isometric diamond outline at a world centre point. */
  private drawDiamond(g: Graphics, cx: number, cy: number): void {
    g.drawPolygon([cx, cy - HH, cx + HW, cy, cx, cy + HH, cx - HW, cy]);
  }

  /** Small coloured pip dots above a claimed venue indicating its bonus types. */
  private drawBonusDots(g: Graphics, cx: number, cy: number, v: VenueNode): void {
    const dots: number[] = [];
    if (v.repBonus   > 0) dots.push(COLOUR_REP);
    if (v.salesBonus > 0) dots.push(COLOUR_SALES);
    if (v.fanBonus   > 0) dots.push(COLOUR_FAN);
    const step   = 8;
    const startX = cx - (step * (dots.length - 1)) / 2;
    for (let i = 0; i < dots.length; i++) {
      g.lineStyle(0);
      g.beginFill(dots[i], 0.9);
      g.drawCircle(startX + i * step, cy, 3);
      g.endFill();
    }
  }

  private bonusLabel(v: VenueNode): string {
    const parts: string[] = [];
    if (v.repBonus   > 0) parts.push(`+${v.repBonus}/s REP`);
    if (v.salesBonus > 0) parts.push(`+${v.salesBonus}/s SALES`);
    if (v.fanBonus   > 0) parts.push(`+${v.fanBonus}/s FAN`);
    return parts.join('  ');
  }

  // ── Fan Workshop build-site rendering ─────────────────────────────────────

  renderBuildSites(sites: FanBuildSite[], elapsed: number): void {
    const g = this.buildG;
    g.clear();

    while (this.buildLabels.length < sites.length) {
      const name = new Text('', NAME_GREY);
      const info = new Text('', INFO_STYLE);
      name.anchor.set(0.5, 1);
      info.anchor.set(0.5, 0);
      this.world.addChild(name, info);
      this.buildLabels.push({ name, info });
    }

    for (let i = 0; i < sites.length; i++) {
      const site  = sites[i];
      const label = this.buildLabels[i];

      if (site.completed) {
        label.name.text = '';
        label.info.text = '';
        continue;
      }

      this.drawBuildSite(g, site, label, elapsed);
    }
  }

  private drawBuildSite(
    g: Graphics, site: FanBuildSite, label: VenueLabel, elapsed: number,
  ): void {
    const pct    = Math.min(site.gearProgress / site.gearRequired, 1);
    const colour = site.active ? COLOUR_BUILD_WORK : COLOUR_BUILD_IDLE;
    // Block height grows from tiny → full as construction progresses
    const h      = 0.3 + pct * 0.8;

    this.drawIsoBlock(g, site.buildingCol, site.buildingRow, h, colour,
      site.active ? 0.80 : 0.45);

    const top = this.buildingTopPoint(site.buildingCol, site.buildingRow, h);

    // Construction progress arc on top
    if (pct > 0) {
      const arcCY  = top.y + HH * 0.5;
      const radius = HW * 0.35;
      const pulse  = site.active ? (Math.sin(elapsed * Math.PI * 3) + 1) / 2 : 0;
      g.lineStyle(0);
      g.beginFill(COLOUR_BUILD_WORK, 0.28 + pulse * 0.22);
      g.moveTo(top.x, arcCY);
      g.arc(top.x, arcCY, radius,
        -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * pct);
      g.closePath();
      g.endFill();
      g.lineStyle(1.5, COLOUR_BUILD_WORK, 0.7 + pulse * 0.2);
      g.drawCircle(top.x, arcCY, radius);
      g.lineStyle(0);
    }

    // GEAR progress bar above the block
    const barW = HW * 1.6;
    const barX = top.x - barW / 2;
    const barY = top.y - 4;
    g.lineStyle(0);
    g.beginFill(0x1a1a1a, 0.75);
    g.drawRect(barX, barY, barW, 3);
    g.endFill();
    if (pct > 0) {
      g.beginFill(COLOUR_BUILD_WORK, 0.95);
      g.drawRect(barX, barY, barW * pct, 3);
      g.endFill();
    }

    // Fan-worker dots orbiting the building
    for (let i = 0; i < Math.min(site.fanCount, 4); i++) {
      const angle = (i / Math.max(site.fanCount, 1)) * Math.PI * 2 - Math.PI / 2 + elapsed;
      const r     = HW * 0.65;
      g.lineStyle(0);
      g.beginFill(COLOUR_BUILD_WORK, 0.85);
      g.drawCircle(top.x + Math.cos(angle) * r, top.y + Math.sin(angle) * r * 0.5, 3);
      g.endFill();
    }

    // Labels
    label.name.style = site.active ? NAME_BLUE : NAME_GREY;
    label.name.text  = site.name;
    label.name.position.set(top.x, barY - 2);

    const gearLeft = Math.ceil(site.gearRequired - site.gearProgress);
    label.info.text = site.active
      ? `building… ${Math.round(pct * 100)}%`
      : `${gearLeft} GEAR needed`;
    label.info.position.set(top.x, barY + 4);
  }
}
